import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthorization } from 'contexts/AuthContextProvider.jsx';
import { getCachedImageOrFetch } from '../cache/imageCache'

const WorkspacesContext = createContext();

export const useWorkspaces = () => useContext(WorkspacesContext);

export const WorkspacesProvider = ({ children }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceWorkers, setWorkspaceWorkers] = useState([]); // Initialize as empty array
  const { accessToken, authAxios, getLoggedUser } = useAuthorization();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (accessToken) {
        try {
          // Fetch workspaces
          const workspaceResponse = await authAxios({
            method: 'get',
            url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/`,
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          setWorkspaces(workspaceResponse.data);

          if (workspaceResponse.data.length !== 0) {
            // Fetch workspace workers for the first workspace
            const workspaceWorkersResponse = await authAxios({
              method: 'get',
              url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspaceResponse.data[0].id}/workers/?compress=true`,
              headers: { Authorization: `Bearer ${accessToken}` },
            });
            const workersData = workspaceWorkersResponse.data;
            // For each worker, fetch (or retrieve from cache) the image URL
            const updatedWorkers = await Promise.all(
              workersData.map(async (worker) => {
                const imageUrl = worker.image;
                const cachedUrl = await getCachedImageOrFetch(imageUrl);
                return { ...worker, image: cachedUrl };
              })
            );
            setWorkspaceWorkers(updatedWorkers);
          }
        } catch (error) {
          console.error('Error fetching workspace data:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [accessToken, authAxios]);

  // Get the currently logged-in user
  const getCurrentlyLoggedUser = () => {
    if (loading || workspaceWorkers.length === 0) return null; // Ensure data is loaded
    const loggedUserID = getLoggedUser();
    return workspaceWorkers.find((worker) => worker.id === loggedUserID) || null;
  };

  // Get logged user permissions
  const getLoggedUserPermissions = () => {
    const currentUser = getCurrentlyLoggedUser();
    return currentUser
      ? {
          is_admin: currentUser.is_admin,
          is_owner: currentUser.is_owner,
          is_staff: currentUser.is_staff,
          user_id: currentUser.id,
        }
      : { is_admin: false, is_owner: false, is_staff: false };
  };

  const handleWorkspaceChange = async (id, load_index_page = true) => {
    // Update last accessed workspace
    await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${id}/workspaceAccess/`,
      { workspace: id },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (load_index_page) window.location.href = "/workflow/index";
  };

  return (
    <WorkspacesContext.Provider
      value={{
        getCurrentlyLoggedUser,
        getLoggedUserPermissions,
        workspaceWorkers,
        loading,
        workspaces,
        setWorkspaces,
        handleWorkspaceChange,
      }}
    >
      {children}
    </WorkspacesContext.Provider>
  );
};
